import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import UniversityHeader from '@/components/university/layout/UniversityHeader';
import UniversityFooter from '@/components/university/layout/UniversityFooter';
import SEO from '@/components/university/common/SEO';

const M = '#06060c';
const G = '#d4a843';
const IN = '#0ea5e9';
const BR = 'rgba(255,255,255,0.07)';
const CA = 'rgba(255,255,255,0.04)';
const TX = 'rgba(255,255,255,0.88)';
const MU = 'rgba(255,255,255,0.45)';

interface Certificate {
  id: string;
  cert_type: 'lesson' | 'module' | 'degree' | 'honours';
  degree_slug: string | null;
  module_slug: string | null;
  issued_at: string;
  pdf_url: string | null;
  verification_code: string;
}

const CERT_CONFIG = {
  lesson: { icon: '📌', label: 'university.certs.lesson', labelDefault: 'Lesson Badge', color: '#60a5fa', grad: 'rgba(96,165,250,0.1)' },
  module: { icon: '📜', label: 'university.certs.module', labelDefault: 'Module Certificate', color: '#d4a843', grad: 'rgba(212,168,67,0.1)' },
  degree: { icon: '🎓', label: 'university.certs.degree', labelDefault: 'Degree Certificate', color: '#4ade80', grad: 'rgba(74,222,128,0.1)' },
  honours: { icon: '⭐', label: 'university.certs.honours', labelDefault: 'University Honours', color: '#0ea5e9', grad: 'rgba(14,165,233,0.1)' },
};

function CertCard({ cert, idx, isMobile }: { cert: Certificate; idx: number; isMobile: boolean }) {
  const { t } = useTranslation();
  const cfg = CERT_CONFIG[cert.cert_type];
  const [copying, setCopying] = useState(false);

  function formatSlug(slug: string | null) {
    if (!slug) return '';
    return slug.replace(/^uni-/, '').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(cert.verification_code);
      setCopying(true);
      setTimeout(() => setCopying(false), 2000);
    } catch { }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.06 }}
      whileHover={!isMobile ? { y: -6, boxShadow: `0 20px 40px rgba(0,0,0,0.4)` } : {}}
      style={{
        background: cfg.grad,
        border: `1.5px solid ${cfg.color}33`,
        borderRadius: 24, padding: isMobile ? '20px' : '28px',
        position: 'relative', overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
    >
      {/* Decorative watermark */}
      <div style={{
        position: 'absolute', top: -15, right: -15,
        fontSize: isMobile ? 80 : 100, opacity: 0.05, userSelect: 'none',
        pointerEvents: 'none'
      }}>
        {cfg.icon}
      </div>

      {/* Type badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={{
          background: `${cfg.color}25`, color: cfg.color,
          border: `1px solid ${cfg.color}40`,
          borderRadius: 8, padding: '4px 12px', fontSize: 11, fontWeight: 800,
          textTransform: 'uppercase', letterSpacing: '0.5px'
        }}>
          {cfg.icon} {t(cfg.label, cfg.labelDefault)}
        </span>
      </div>

      {/* Title */}
      <h3 style={{ fontWeight: 800, fontSize: isMobile ? 18 : 22, margin: '0 0 8px', color: '#fff', lineHeight: 1.25, letterSpacing: '-0.4px' }}>
        {formatSlug(cert.degree_slug || cert.module_slug) || 'Still Waters University Credential'}
      </h3>
      <p style={{ color: MU, fontSize: 13, margin: '0 0 24px', fontWeight: 500 }}>
        Issued {new Date(cert.issued_at).toLocaleDateString('en-GB', {
          day: 'numeric', month: 'long', year: 'numeric',
        })}
      </p>

      {/* Verification code */}
      <div style={{
        background: 'rgba(0,0,0,0.25)', borderRadius: 12,
        padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, marginBottom: 20, border: '1px solid rgba(255,255,255,0.05)'
      }}>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ fontSize: 9, color: MU, letterSpacing: '1.2px', marginBottom: 4, fontWeight: 700 }}>
            VERIFICATION CODE
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 14, color: cfg.color, letterSpacing: '1px', fontWeight: 600 }}>
            {cert.verification_code}
          </div>
        </div>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={copyCode}
          style={{
            background: copying ? 'rgba(74,222,128,0.15)' : 'rgba(255,255,255,0.08)',
            border: `1px solid ${copying ? '#4ade8050' : BR}`,
            borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
            fontSize: 12, color: copying ? '#4ade80' : TX, fontWeight: 700,
            transition: 'all 0.2s', flexShrink: 0
          }}
        >
          {copying ? '✓' : 'Copy'}
        </motion.button>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 10 }}>
        {cert.pdf_url ? (
          <motion.a
            href={cert.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            whileTap={{ scale: 0.97 }}
            style={{
              flex: 1, background: cfg.color, color: '#06060c',
              border: 'none', borderRadius: 12, padding: '12px',
              fontSize: 14, fontWeight: 800, cursor: 'pointer',
              textAlign: 'center', textDecoration: 'none', display: 'block',
              boxShadow: `0 8px 20px ${cfg.color}30`,
            }}
          >
            Download PDF
          </motion.a>
        ) : (
          <div style={{
            flex: 1, background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${BR}`,
            borderRadius: 12, padding: '12px',
            fontSize: 13, color: MU, textAlign: 'center', fontWeight: 600
          }}>
            Processing…
          </div>
        )}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            const text = `I earned a ${t(cfg.label, cfg.labelDefault)} from Still Waters University! Link: https://stillwaters.uni/verify/${cert.verification_code}`;
            if (navigator.share) {
              navigator.share({ title: 'My Credential', text }).catch(() => { });
            } else {
              navigator.clipboard.writeText(text);
              alert('Share text copied to clipboard!');
            }
          }}
          style={{
            background: CA, border: `1px solid ${BR}`,
            borderRadius: 12, padding: '12px 18px',
            cursor: 'pointer', fontSize: 16,
          }}
        >
          🔗
        </motion.button>
      </div>
    </motion.div>
  );
}

export default function UniversityCertificatesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isLoading: authLoading } = useAuth();

  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'degree' | 'module' | 'honours'>('all');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 820);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate('/signin', { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const { data } = await supabase
          .from('university_certificates')
          .select('id, cert_type, degree_slug, module_slug, issued_at, pdf_url, verification_code')
          .eq('user_id', user!.id)
          .order('issued_at', { ascending: false });

        if (cancelled) return;
        setCertificates((data || []) as Certificate[]);
      } catch (err) {
        console.error('CertificatesPage load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user]);

  const filtered = filter === 'all' ? certificates : certificates.filter(c => c.cert_type === filter);

  const counts = {
    degree: certificates.filter(c => c.cert_type === 'degree').length,
    module: certificates.filter(c => c.cert_type === 'module').length,
    honours: certificates.filter(c => c.cert_type === 'honours').length,
  };

  if (authLoading || loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: M }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
          style={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid rgba(212,168,67,0.15)`, borderTopColor: G }}
        />
      </div>
    );
  }

  return (
    <>
    <UniversityHeader/>
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO title="Certificates" noindex />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '32px 16px 80px' : '40px 24px 80px' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}>
          <button
            onClick={() => navigate('/university/dashboard')}
            style={{
              background: 'none',
              border: 'none',
              color: MU,
              cursor: 'pointer',
              fontSize: 13,
              marginBottom: 16,
              padding: 0,
              fontWeight: 600
            }}
          >
            {t('university.certs.backToDashboard', '← BACK TO DASHBOARD')}
          </button>
          <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '2.4px', marginBottom: 8, textTransform: 'uppercase' }}>
            {t('university.certs.eyebrow', 'STILL WATERS UNIVERSITY')}
          </p>
          <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 900, margin: '0 0 12px', letterSpacing: '-0.8px', lineHeight: 1.1, color: '#fff' }}>
            {t('university.certs.title', 'Your Credentials')}
          </h1>
          <p style={{ color: MU, fontSize: 16, margin: '0 0 40px', fontWeight: 500 }}>
            {t('university.certs.subtitle', 'Every credential is cryptographically signed and verifiable.')}
          </p>
        </motion.div>

        {/* Stats row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? 12 : 20,
          marginBottom: 40
        }}>
          {[
            { icon: '🎓', label: t('university.certs.statDegrees', 'Degree Certificates'), value: counts.degree, color: '#4ade80' },
            { icon: '📜', label: t('university.certs.statModules', 'Module Certificates'), value: counts.module, color: G },
            { icon: '⭐', label: t('university.certs.statHonours', 'Honours Credentials'), value: counts.honours, color: '#0ea5e9' },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              style={{
                background: CA, border: `1px solid ${BR}`,
                borderRadius: 20, padding: isMobile ? '20px' : '24px',
                display: 'flex', alignItems: 'center', gap: 16,
              }}
            >
              <span style={{ fontSize: isMobile ? 26 : 32 }}>{s.icon}</span>
              <div>
                <div style={{ fontWeight: 900, fontSize: isMobile ? 24 : 32, color: s.color, letterSpacing: '-1px', lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: MU, fontWeight: 700, textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 28, background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 4, border: `1px solid ${BR}`, width: 'fit-content' }}>
          {(['all', 'degree', 'module', 'honours'] as const).map(f => (
            <motion.button
              key={f}
              onClick={() => setFilter(f)}
              whileHover={{ scale: 1.02 }}
              style={{
                background: filter === f ? G : 'transparent',
                color: filter === f ? '#06060c' : MU,
                border: 'none', padding: '8px 20px', borderRadius: 8,
                cursor: 'pointer', fontSize: 13, fontWeight: 700, transition: 'all 0.15s',
              }}
            >
              {f === 'all' ? t('university.certs.all', 'All')
                : f === 'degree' ? t('university.certs.degrees', 'Degrees')
                  : f === 'module' ? t('university.certs.modules', 'Modules')
                    : t('university.certs.honoursTab', 'Honours')}
            </motion.button>
          ))}
        </div>

        {/* Certificate grid or empty state */}
        <AnimatePresence mode="wait">
          {filtered.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                background: CA, border: `1px solid ${BR}`,
                borderRadius: 20, padding: '80px', textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 56, marginBottom: 20 }}>🏆</div>
              <h3 style={{ fontWeight: 700, fontSize: 22, marginBottom: 12 }}>
                {certificates.length === 0
                  ? t('university.certs.none', 'No certificates yet')
                  : t('university.certs.noneFilter', 'None in this category')}
              </h3>
              <p style={{ color: MU, fontSize: 15, maxWidth: 400, margin: '0 auto 32px', lineHeight: 1.7 }}>
                {certificates.length === 0
                  ? t('university.certs.noneHint', 'Complete a lesson module with 70%+ to earn your first certificate.')
                  : t('university.certs.noneFilterHint', 'Keep learning to earn certificates in this category.')}
              </p>
              {certificates.length === 0 && (
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  onClick={() => navigate('/university/dashboard')}
                  style={{
                    background: `linear-gradient(135deg, ${G}, #b8912e)`,
                    color: '#06060c', border: 'none', borderRadius: 12,
                    padding: '14px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  {t('university.certs.startLearning', 'Start Learning')} →
                </motion.button>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}
            >
              {filtered.map((cert, i) => (
                <CertCard key={cert.id} cert={cert} idx={i} isMobile={isMobile} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
      <UniversityFooter />
    </>
  );
}
